import { useState, useRef, useEffect } from 'react';

interface Service {
  id: number;
  title: string;
  icon: string;
  description: string;
  image: string;
}

const services: Service[] = [
  {
    id: 1,
    title: 'Instalaciones Eléctricas',
    icon: 'ri-plug-2-line',
    description: 'Instalaciones completas certificadas para hogares y empresas. Cuadros eléctricos, cableado, enchufes y circuitos con los más altos estándares de seguridad.',
    image: 'https://public.readdy.ai/ai/img_res/81d0b100606724b32840df0c05997738.jpg'
  },
  {
    id: 2,
    title: 'Reparaciones Urgentes 24/7',
    icon: 'ri-tools-line',
    description: 'Servicio de emergencia disponible las 24 horas. Cortocircuitos, averías, caídas de tensión y cualquier problema eléctrico crítico resuelto con rapidez.',
    image: 'https://readdy.ai/api/search-image?query=emergency%20electrician%20repairing%20electrical%20fault%20at%20night%2C%20technician%20fixing%20short%20circuit%20with%20professional%20tools%2C%20urgent%20electrical%20repair%20service%20with%20dramatic%20dark%20lighting%20and%20yellow%20sparks%2C%20detailed%20technical%20work%20scene%2C%20high%20contrast%20moody%20photography&width=400&height=550&seq=srv-urgent-2&orientation=portrait'
  },
  {
    id: 3,
    title: 'Diseño de Iluminación LED',
    icon: 'ri-lightbulb-flash-line',
    description: 'Proyectos de iluminación interior con tiras LED, focos empotrables y sistemas regulables. Creamos ambientes únicos adaptados a cada espacio.',
    image: 'https://readdy.ai/api/search-image?query=stunning%20interior%20LED%20strip%20lighting%20design%20in%20modern%20living%20room%2C%20warm%20glowing%20LED%20strips%20under%20ceiling%20coving%20and%20furniture%2C%20contemporary%20home%20with%20dramatic%20ambient%20lighting%2C%20architectural%20photography%20with%20rich%20warm%20tones%2C%20beautiful%20interior%20design%20with%20custom%20LED%20illumination&width=400&height=550&seq=srv-led-3&orientation=portrait'
  },
  {
    id: 4,
    title: 'Cargadores Vehículo Eléctrico',
    icon: 'ri-charging-pile-2-line',
    description: 'Instalación de puntos de recarga para coches eléctricos en garaje o empresa. Gestión inteligente de energía y máxima seguridad.',
    image: 'https://readdy.ai/api/search-image?query=electric%20vehicle%20charging%20station%20installed%20in%20modern%20home%20garage%2C%20professional%20EV%20charger%20mounted%20on%20dark%20wall%2C%20contemporary%20charging%20point%20with%20sleek%20minimalist%20design%2C%20professional%20photography%20with%20clean%20dramatic%20lighting%2C%20detailed%20technical%20installation%20scene&width=400&height=550&seq=srv-ev-4&orientation=portrait'
  },
  {
    id: 5,
    title: 'Seguridad & Cámaras',
    icon: 'ri-camera-line',
    description: 'Sistemas de videovigilancia HD con acceso remoto, grabación en la nube y alertas en tiempo real para tu hogar o negocio.',
    image: 'https://readdy.ai/api/search-image?query=modern%20security%20camera%20system%20installation%20on%20building%20exterior%20at%20night%2C%20professional%20surveillance%20equipment%20with%20advanced%20technology%2C%20contemporary%20security%20setup%20with%20dark%20moody%20atmosphere%2C%20professional%20photography%20with%20architectural%20detail%2C%20high-tech%20security%20installation%20with%20dramatic%20lighting&width=400&height=550&seq=srv-cam-5&orientation=portrait'
  },
  {
    id: 6,
    title: 'Certificaciones Oficiales',
    icon: 'ri-file-shield-2-line',
    description: 'Inspecciones técnicas y certificados oficiales para instalaciones eléctricas. Documentación reglamentaria para viviendas, locales y comunidades.',
    image: 'https://readdy.ai/api/search-image?query=professional%20electrical%20inspection%20and%20certification%20process%2C%20technician%20reviewing%20electrical%20panel%20with%20testing%20equipment%2C%20official%20certification%20documentation%20scene%2C%20professional%20photography%20with%20clean%20dramatic%20lighting%2C%20detailed%20technical%20inspection%20work%20in%20dark%20industrial%20setting&width=400&height=550&seq=srv-cert-6&orientation=portrait'
  }
];

export default function Services() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const visibleCount = isMobile ? 1 : 3;
  const maxIndex = services.length - visibleCount;

  const goTo = (index: number) => {
    const clamped = Math.max(0, Math.min(index, maxIndex));
    setActiveIndex(clamped);
  };

  useEffect(() => {
    autoRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 3500);
    return () => {
      if (autoRef.current) clearInterval(autoRef.current);
    };
  }, [maxIndex]);

  const resetAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 3500);
  };

  const handlePrev = () => { goTo(activeIndex - 1); resetAuto(); };
  const handleNext = () => { goTo(activeIndex + 1); resetAuto(); };
  const handleDot = (i: number) => { goTo(i); resetAuto(); };

  const gapRem = isMobile ? 1 : 1.5;

  return (
    <section id="servicios" className="relative py-16 md:py-28 px-4 md:px-8 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="inline-block border border-gray-600 text-gray-400 px-4 py-2 rounded-lg text-sm mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            (Nuestros Servicios)
          </span>
        </div>
        <h2 className="text-white font-bold text-3xl md:text-6xl text-center mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Todo lo que necesitas,
        </h2>
        <h2 className="text-[#FFD700] font-bold text-3xl md:text-6xl text-center mb-10 md:mb-14" style={{ fontFamily: 'Poppins, sans-serif' }}>
          en un solo equipo
        </h2>

        {/* Carousel wrapper */}
        <div className="relative px-8 md:px-10">
          {/* Prev button */}
          <button
            onClick={handlePrev}
            disabled={activeIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-[#FFD700] rounded-full shadow-lg shadow-[#FFD700]/40 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-s-line text-black text-xl"></i>
          </button>

          {/* Track */}
          <div className="overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                gap: `${gapRem}rem`,
                transform: `translateX(calc(-${activeIndex} * (100% / ${visibleCount}) - ${activeIndex} * ${gapRem}rem / ${visibleCount}))`
              }}
            >
              {services.map((service) => (
                <div
                  key={service.id}
                  className="relative rounded-3xl overflow-hidden cursor-pointer flex-shrink-0 transition-all duration-500 hover:scale-[1.02]"
                  style={{
                    width: `calc((100% - ${(visibleCount - 1) * gapRem}rem) / ${visibleCount})`,
                    minWidth: `calc((100% - ${(visibleCount - 1) * gapRem}rem) / ${visibleCount})`,
                    aspectRatio: '3/4',
                    minHeight: isMobile ? '300px' : '360px'
                  }}
                  onMouseEnter={() => setHoveredId(service.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => setHoveredId(hoveredId === service.id ? null : service.id)}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>

                  <div className="relative z-10 h-full flex flex-col justify-between p-5 md:p-7">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                      <div className="bg-[#FFD700] rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center shadow-lg shadow-[#FFD700]/50">
                        <i className={`${service.icon} text-black text-lg md:text-xl`}></i>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-white font-semibold text-base md:text-xl mb-2 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {service.title}
                      </h3>
                      <p
                        className={`text-gray-300 text-sm leading-relaxed transition-all duration-500 ${
                          hoveredId === service.id ? 'opacity-100 max-h-32' : 'opacity-0 max-h-0'
                        } overflow-hidden`}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={handleNext}
            disabled={activeIndex >= maxIndex}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-[#FFD700] rounded-full shadow-lg shadow-[#FFD700]/40 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-s-line text-black text-xl"></i>
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => handleDot(i)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                i === activeIndex ? 'w-8 h-2 bg-[#FFD700]' : 'w-2 h-2 bg-gray-600 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
